import { Button } from '@/components/ui/button'
import { checkLogin, useCurrentPubkey } from '@/atoms/session'
import client from '@/services/client.service'
import type { RelayInfoDocument } from '@nostr/gadgets/relays'
import { HugeiconsIcon } from '@hugeicons/react'
import { Login01Icon, Logout01Icon, Mail01Icon } from '@hugeicons/core-free-icons'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import InviteCodeDialog from './InviteCodeDialog'
import JoinDialog from './JoinDialog'
import { useAtomValue } from 'jotai'
import { unwrappedMembershipsByPubkeyAtom, relayMembersAtomFamily } from '@/atoms/memberships'

export default function RelayMembershipControl({ relayInfo }: { relayInfo: RelayInfoDocument }) {
  const { t } = useTranslation()
  const pubkey = useCurrentPubkey()
  const [isLoading, setIsLoading] = useState(false)
  const [showJoinDialog, setShowJoinDialog] = useState(false)
  const [showInviteCodeDialog, setShowInviteCodeDialog] = useState(false)
  const supportsNip43 = useMemo(
    () => relayInfo.supported_nips?.includes?.(43) && !!relayInfo.self,
    [relayInfo]
  )

  const memberships = useAtomValue(unwrappedMembershipsByPubkeyAtom)
  const isMember = useMemo(
    () => !!pubkey && !!memberships?.get(pubkey)?.has(relayInfo.url),
    [relayInfo.url, memberships, pubkey]
  )

  if (!supportsNip43 || !memberships) {
    return null
  }

  return (
    <>
      {isMember ? (
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="secondary"
            className="w-full"
            onClick={handleGetInviteCodeClick}
            disabled={isLoading}
          >
            <HugeiconsIcon icon={Mail01Icon} className="w-4 h-4 mr-2" />
            {t('Get Invite Code')}
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={handleLeaveClick}
            disabled={isLoading}
          >
            <HugeiconsIcon icon={Logout01Icon} className="w-4 h-4 mr-2" />
            {t('Leave')}
          </Button>
        </div>
      ) : (
        <Button
          variant="default"
          className="w-full"
          onClick={() => {
            checkLogin(() => setShowJoinDialog(true))
          }}
          disabled={isLoading}
        >
          <HugeiconsIcon icon={Login01Icon} className="w-4 h-4 mr-2" />
          {t('Request to Join Relay')}
        </Button>
      )}

      <JoinDialog
        relayInfo={relayInfo}
        showJoinDialog={showJoinDialog}
        setShowJoinDialog={setShowJoinDialog}
      />

      <InviteCodeDialog
        relayInfo={relayInfo}
        showInviteCodeDialog={showInviteCodeDialog}
        setShowInviteCodeDialog={setShowInviteCodeDialog}
      />
    </>
  )

  function handleGetInviteCodeClick() {
    setShowInviteCodeDialog(true)
  }

  async function handleLeaveClick() {
    if (!relayInfo.self || !confirm(t('Are you sure you want to leave this relay?'))) {
      return
    }

    setIsLoading(true)
    try {
      const leaveRequestEvent = await client.publish(
        {
          kind: 28936,
          created_at: Math.floor(Date.now() / 1000),
          tags: [['-']],
          content: ''
        },
        {
          specifiedRelayUrls: [relayInfo.url]
        }
      )
      if (!leaveRequestEvent) throw new Error('no signer connected')

      toast.success(t('Leave request sent successfully'))
      relayMembersAtomFamily.remove(relayInfo.url)
      relayMembersAtomFamily(relayInfo.url)
    } catch (error: any) {
      const errors = error instanceof AggregateError ? error.errors : [error]
      for (const err of errors) {
        toast.error(
          `${t('Failed to send leave request')}: ${err instanceof Error ? err.message : String(err)}`,
          { duration: 10_000 }
        )
        console.error(err)
      }
      return
    } finally {
      setIsLoading(false)
    }
  }
}
